"use client";

import dynamic from "next/dynamic";

import { useCallback, useEffect, useMemo, useState } from "react";
import { getProduct } from "@/functions/databaseData";
import useModal from "@/functions/useModal";

import DetailProductSeceleton from "@/components/seceleton/DetailProduct";
import { BlurImage } from "@/public/images";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { notFound } from "next/navigation";

import Image from "next/image";
import {
  CheckboxCheckedSvg,
  CheckboxUncheckSvg,
  ChevronDownSvg,
  ChevronUpSvg,
  CloseOutlineSvg,
  CloseSolidSvg
} from "@/public/svg";
import Container from "@/components/atom/Container";

import Crumbs from "@/components/atom/Crumbs";
import Divider from "@/components/atom/Divider";
import useHeader from "@/functions/useHeader";
import { cn } from "@/utils/twMerge";
import DetailProduct from "./_component/DetailProduct";
import DescProduct from "./_component/DescProduct";
import ContainerModal from "@/components/sub-atom/ContainerModal";
import { format } from "date-fns";
import Button from "@/components/atom/Button";
import { toast } from "react-toastify";
import { rateLimiterDownloadIMG } from "@/functions/rateLimiter";

const SliderImage = dynamic(
  () => import("./_component/SliderImage").then((module) => module.default),
  {
    ssr: false,
    loading: () => (
      <div className="flex flex-col gap-12 px-gon">
        <div className="flex w-full h-[400px] object-cover animate-pulse bg-greyscaleGON-200" />
      </div>
    )
  }
);

const MySwiper = dynamic(
  () => import("@/components/atom/Swiper").then((module) => module.default),
  {
    ssr: false
  }
);

export default function Page({ params }) {
  const { session, status, profile, carts } = useHeader();
  const { product_id } = params;
  const [dataNotFound, setDataNotFound] = useState(false);
  const [myData, setMyData] = useState();
  const [myDataWithAuth, setMyDataWithAuth] = useState();
  const [loading, setLoading] = useState({
    runCarts: false,
    runProduct: false
  });
  const [selected, setSelected] = useState({});
  const { openModal, closeModal } = useModal();
  const [fetchTrigger, setFetchTrigger] = useState(false);
  const [downloadThumb, setDownloadThumb] = useState({
    outputFormat: "",
    show: false
  });

  const [activeThumbImage, setActiveThumbImage] = useState();

  const fetchImageWithRateLimit = rateLimiterDownloadIMG(
    !profile ? null : profile?.mitra_id,
    {
      limit: 19,
      windowMs: 1000 // 1 detik
    }
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!session) {
      if (!myData) runGetProduct();
    } else {
      if (!myDataWithAuth) runGetProduct(session?.user?.token);
    }
  }, [product_id?.[0], fetchTrigger, session?.user?.token]);

  const runGetProduct = useCallback(
    async (props) => {
      closeModal();
      setLoading((old) => ({
        ...old,
        runProduct: true
      }));
      try {
        const data = await getProduct({
          id: product_id?.[0]?.split("-")?.reverse()?.[0],
          token: props,
          methodNextFetch: {
            cache: "no-store"
          }
        });

        if (!data) return setDataNotFound(true);
        if (!props) {
          return setMyData(data);
        }
        setMyDataWithAuth(data);
      } catch (error) {
        console.log(error.message);
      } finally {
        setLoading((old) => ({
          ...old,
          runProduct: false
        }));
      }
    },
    [session, product_id?.[0], fetchTrigger]
  );

  const datas = useMemo(() => {
    return !myDataWithAuth ? myData : myDataWithAuth;
  }, [myDataWithAuth, myData]);

  const { thumbnails, slideTo } = useMemo(() => {
    const thumbnails = datas?.thumbnails
      ?.map((val) =>
        val === '""' || val === '"' || val === ""
          ? BlurImage
          : val == "-" ||
            !(val && (val.startsWith("http") || val.startsWith("/")))
          ? !datas?.thumbnails_main
            ? ""
            : datas?.thumbnails_main
          : val
      )
      ?.map((val) => val);

    const isClickVariant = selected?.thumbnails ?? selected?.[0]?.thumbnails;
    const slideTo = !selected?.index
      ? thumbnails?.indexOf(isClickVariant)
      : selected?.index;

    setActiveThumbImage(slideTo < 0 ? 0 : slideTo);
    return {
      thumbnails,
      slideTo
    };
  }, [selected, datas?.thumbnails]);

  const handleDownload = async (
    all = false,
    outputFormat = "jpeg",
    url = "",
    outputFilename = ""
  ) => {
    async function run(imageUrl, outputFormat, all = false) {
      setLoading((old) => ({ ...old, download: true }));
      try {
        const isOutputFilename = `${
          !outputFilename ? activeThumbImage : outputFilename
        }-${format(Date.now(), "hh-mm-ss")?.replaceAll("-", "")}`;
        const imageBlob = await fetchImageWithRateLimit(imageUrl);

        var a = document.createElement("A");
        a.href = URL.createObjectURL(imageBlob);
        a.download = `${isOutputFilename}.${outputFormat}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        console.error("Error:", error?.message);
        toast.error(error?.message, {
          position: "top-center"
        });
      } finally {
        all ? null : setLoading((old) => ({ ...old, download: false }));
      }
    }

    const imageUrl = !url ? thumbnails?.[activeThumbImage] : url;
    if (!all) run(imageUrl, outputFormat);
    else {
      async function runWithInterval(
        thumbnails,
        outputFormat,
        interval = 3000
      ) {
        setLoading((old) => ({ ...old, download: true }));
        let index = 0;
        const intervalId = setInterval(async () => {
          if (index < thumbnails.length) {
            try {
              await run(thumbnails[index], outputFormat, true);
              index++;
            } catch (error) {
              console.error("Error:", error);
              setLoading((old) => ({ ...old, download: false }));
              clearInterval(intervalId);
            }
          } else {
            setLoading((old) => ({ ...old, download: false }));
            clearInterval(intervalId);
          }
        }, interval);
      }
      runWithInterval(thumbnails, outputFormat);
    }
  };

  return (
    <div className={cn("flex flex-col justify-between", "mt-32")}>
      <Container showMQ={false} className="z-10">
        <div className="container-mini pb-10 w-full">
          <Crumbs />
          {dataNotFound ? (
            notFound()
          ) : (
            <div className="flex flex-col gap-10">
              <div className="flex flex-col sm:flex-row w-full gap-4 xs:gap-8">
                {loading.runProduct || !datas || status == "loading" ? (
                  <div className="flex w-full sm:max-w-[400px] h-[400px] object-cover animate-pulse bg-greyscaleGON-200" />
                ) : (
                  <div className="flex w-full sm:max-w-[400px]">
                    <SliderImage
                      item={datas}
                      selected={selected}
                      setSelected={setSelected}
                      loading={loading.runProduct}
                      handleModal={openModal}
                    />
                  </div>
                )}
                <div className="flex w-full">
                  {loading.runProduct ||
                  !datas ||
                  status == "loading" ||
                  !datas?.variants ? (
                    <DetailProductSeceleton />
                  ) : (
                    <DetailProduct
                      item={datas}
                      user={session?.user?.data}
                      profile={profile}
                      carts={carts}
                      setSelected={setSelected}
                      handleModal={openModal}
                      fetchTrigger={fetchTrigger}
                      setFetchTrigger={setFetchTrigger}
                    />
                  )}
                </div>
              </div>
              <Divider />
              <DescProduct
                item={datas}
                handleDownload={handleDownload}
                loading={loading}
              />
            </div>
          )}
        </div>
      </Container>
      <ContainerModal
        className={
          "flex max-w-[500px] h-screen xs:h-auto max-h-screen  flex-col gap-2 sm:gap-4"
        }
      >
        <div className="flex w-full h-full flex-col justify-center items-center">
          <div className="flex flex-row w-full mb-6 justify-between items-center">
            <p className="subheading-semiBold text-greyscaleGON-700">
              {datas?.name_product}
            </p>
            <div
              className="text-greyscaleGON-700 cursor-pointer select-none"
              onClick={(e) => {
                e.preventDefault();
                setDownloadThumb({});
                closeModal();
              }}
            >
              <CloseOutlineSvg className="h-6 w-6 xs:h-8 xs:w-8" />
            </div>
          </div>

          {!session?.user?.token ? null : (
            <div className="flex flex-col gap-2 mb-2 w-full">
              <div className="flex py-2 flex-col gap-2 sm:flex-row justify-between sm:items-center">
                <div
                  className={
                    ("flex w-full sm:w-1/2 flex-row items-center gap-2 select-none",
                    loading?.download || downloadThumb?.show
                      ? "cursor-not-allowed"
                      : "cursor-pointer")
                  }
                  onClick={() =>
                    loading?.download
                      ? {}
                      : !downloadThumb?.show && handleDownload(false, "jpeg")
                  }
                >
                  <p
                    className={cn(
                      "p3-semiBold",
                      loading?.download && !downloadThumb?.show
                        ? "text-primaryGON-300"
                        : downloadThumb?.show
                        ? "text-primaryGON-100"
                        : "text-primaryGON-400"
                    )}
                  >
                    {loading?.download && !downloadThumb?.show
                      ? "loading..."
                      : "Download Gambar"}
                  </p>
                </div>
                <div
                  className="flex w-full sm:w-1/2 flex-row items-center sm:justify-end gap-2 cursor-pointer select-none"
                  onClick={() =>
                    setDownloadThumb({ show: !downloadThumb?.show })
                  }
                >
                  <p className="p3-semiBold text-greyscaleGON-700">
                    Setting Download Gambar
                  </p>
                  {!downloadThumb?.show ? (
                    <ChevronDownSvg className="w-4 h-4 text-greyscaleGON-700" />
                  ) : (
                    <ChevronUpSvg className="w-4 h-4 text-greyscaleGON-700" />
                  )}
                </div>
              </div>
              {downloadThumb?.show && (
                <div className="flex flex-col gap-2 w-full p-2 border">
                  <p className="text-[10px] font-poppins text-greyscaleGON-400">
                    Format Gambar
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 w-full">
                    {["webp", "jpeg", "png", "tiff"]?.map((a, b) => {
                      return (
                        <p
                          onClick={() =>
                            setDownloadThumb((old) => ({
                              ...old,
                              outputFormat: a
                            }))
                          }
                          key={b}
                          className={cn(
                            "p3-semiBold uppercase cursor-pointer select-none  hover:text-primaryGON-400 text-center p-2 border rounded hover:bg-primaryGON-100 hover:ring-[0.9] hover:ring-primaryGON-400",
                            downloadThumb?.outputFormat == a
                              ? "text-primaryGON-400 bg-primaryGON-100"
                              : ""
                          )}
                        >
                          {a == "jpeg" ? "JPEG/JPG" : a}
                        </p>
                      );
                    })}
                  </div>
                  <Divider />
                  <div
                    className="flex flex-row items-center gap-2 w-fit cursor-pointer select-none "
                    onClick={() =>
                      setDownloadThumb((old) => ({
                        ...old,
                        all: !downloadThumb?.all
                      }))
                    }
                  >
                    {downloadThumb?.all ? (
                      <CheckboxCheckedSvg className="w-4 h-4 text-primaryGON-400" />
                    ) : (
                      <CheckboxUncheckSvg className="w-4 h-4 text-primaryGON-100" />
                    )}
                    <p className="p3-regular text-greyscaleGON-700">
                      Download semua gambar
                    </p>
                  </div>
                  <Button
                    label="Download"
                    disable={loading?.download || !downloadThumb?.outputFormat}
                    loading={loading?.download}
                    size="small"
                    onClick={() =>
                      handleDownload(
                        downloadThumb?.all,
                        downloadThumb?.outputFormat
                      )
                    }
                  />
                </div>
              )}
            </div>
          )}
          <div className="flex gap-10 w-full justify-center h-full">
            <div className="aspect-[4/5] overflow-hidden items-center justify-center place-items-center">
              <MySwiper
                loop={false}
                autoplay={false}
                items={thumbnails}
                navigation={false}
                handleOnSlideChange={(a) => setActiveThumbImage(a?.activeIndex)}
                // showThumb
                noEffect
                slideTo={slideTo}
                className={"w-full h-full"}
                onSwiper={(val) => val?.slideTo(selected?.index)}
                rendeitem={({ item, index }) =>
                  !item ? (
                    <Image
                      unoptimized
                      src={BlurImage}
                      alt={`${index}-Image`}
                      width={320}
                      height={320}
                      className="object-cover object-top w-auto h-auto"
                      blurDataURL={BlurImage?.blurDataURL}
                      placeholder="blur"
                    />
                  ) : (
                    <Link
                      target="_blank"
                      href={item ?? "/"}
                      aria-label="privacy"
                      passHref
                    >
                      <Image
                        src={item}
                        alt={`${index}-Image`}
                        width={320}
                        height={320}
                        className="object-cover object-top w-auto h-auto"
                        blurDataURL={BlurImage?.blurDataURL}
                        placeholder="blur"
                      />
                    </Link>
                  )
                }
              />
            </div>
          </div>
        </div>
      </ContainerModal>
    </div>
  );
}
